import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Bell,
  Check,
  CheckCheck,
  Trash2,
  AlertCircle,
  CheckCircle,
  Info,
  AlertTriangle,
  Clock,
  Megaphone,
  FileText,
  Briefcase,
  Settings,
} from 'lucide-react';
import { useNotifications, Notification } from '@/hooks/useNotifications';
import { useAuth } from '@/contexts/AuthContext.hook';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export const NotificationDropdown: React.FC = () => {
  const navigate = useNavigate();
  const { profile, userRoles } = useAuth();
  const {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  } = useNotifications();

  const getNotificationIcon = (n: Notification) => {
    // Broadcasts admin-driven usam ícone por categoria
    if (n.category) {
      switch (n.category) {
        case 'importante':
          return <AlertTriangle className="w-4 h-4 text-red-500" />;
        case 'atualizacao':
          return <Info className="w-4 h-4 text-blue-500" />;
        case 'documento':
          return <FileText className="w-4 h-4 text-orange-500" />;
        case 'novo_investimento':
          return <Briefcase className="w-4 h-4 text-green-500" />;
      }
    }
    switch (n.type) {
      case 'approval':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'rejection':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
      case 'success':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'communication':
        return <Megaphone className="w-4 h-4 text-purple-500" />;
      default:
        return <Info className="w-4 h-4 text-blue-500" />;
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.read) {
      await markAsRead(notification.id);
    }

    // Admin broadcast com URL explícito → navega direto (deep-link)
    if (notification.url) {
      navigate(notification.url);
      return;
    }

    // Admin broadcast sem URL (ex.: categoria=atualizacao) → não navega.
    // Usuário leu na própria dropdown; clicar só marca como lido.
    if (notification.category) {
      return;
    }

    // Fallback legado: comunicações antigas (sem category) vão para /comunicados
    if (notification.type === 'communication') {
      const userRole = userRoles[0]?.role || 'investidor';
      const basePath = userRole === 'admin' ? '/admin'
        : userRole === 'assessor' ? '/assessor'
        : userRole === 'partner' ? '/partner'
        : userRole === 'mfb' ? '/mfb'
        : userRole === 'mfa' ? '/mfa'
        : userRole === 'incorporador' ? '/incorporador'
        : '/investor';
      navigate(`${basePath}/comunicados`);
    }
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  const handleDeleteNotification = async (notificationId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    await deleteNotification(notificationId);
  };

  const formatTime = (dateString: string) => {
    return formatDistanceToNow(new Date(dateString), {
      addSuffix: true,
      locale: ptBR
    });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="relative">
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-[22rem] max-w-[90vw] p-0 flex flex-col max-h-[min(32rem,80vh)]">
        <DropdownMenuLabel className="flex items-center justify-between px-3 py-2 shrink-0">
          <span>Notificações</span>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleMarkAllAsRead}
              className="h-auto p-1 text-xs"
            >
              <CheckCheck className="w-3 h-3 mr-1" />
              Marcar todas
            </Button>
          )}
        </DropdownMenuLabel>

        <DropdownMenuSeparator className="m-0" />

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Nenhuma notificação</p>
          </div>
        ) : (
          <ScrollArea className="flex-1 min-h-0">
            {notifications.map((notification) => (
              <DropdownMenuItem
                key={notification.id}
                className={`p-3 cursor-pointer transition-colors ${
                  !notification.read ? 'bg-muted/50' : ''
                }`}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="flex items-start gap-3 w-full">
                  <div className="shrink-0 mt-0.5">
                    {getNotificationIcon(notification)}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-medium truncate">
                        {notification.title}
                      </p>
                      <div className="flex items-center gap-1 shrink-0">
                        {!notification.read && (
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-auto p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={(e) => handleDeleteNotification(notification.id, e)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>

                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                      {notification.message}
                    </p>

                    <div className="flex items-center gap-1 mt-2">
                      <Clock className="w-3 h-3 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">
                        {formatTime(notification.created_at)}
                      </span>
                    </div>
                  </div>
                </div>
              </DropdownMenuItem>
            ))}
          </ScrollArea>
        )}

        <div className="shrink-0">
          <DropdownMenuSeparator className="m-0" />
          <DropdownMenuItem
            className="cursor-pointer text-sm px-3 py-2"
            onClick={() => {
              const userRole = userRoles[0]?.role || 'investidor';
              const basePath = userRole === 'admin' ? '/admin'
                : userRole === 'assessor' ? '/assessor'
                : userRole === 'partner' ? '/partner'
                : userRole === 'mfb' ? '/mfb'
                : userRole === 'mfa' ? '/mfa'
                : userRole === 'incorporador' ? '/incorporador'
                : '/investor';
              navigate(`${basePath}/profile#notificacoes`);
            }}
          >
            <Settings className="w-4 h-4 mr-2" />
            Configurar notificações
          </DropdownMenuItem>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
